// skills/husbandry/husbandry.js
// Husbandry skill logic - passive pet management (NO active action)

import {
    PETS,
    PET_CONTRACTS,
    EVOLUTION_COSTS,
    EVOLUTION_BONUSES,
    getFeedSpeedBonus,
    getContractsForPet,
    getPetData,
    getEvolutionCost,
    getMaxFood,
    getRequiredFood,
    HUSBANDRY_CONFIG,
    getContractTierRewards
} from './husbandryData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS, PET_CONFIG, HUSBANDRY_CONFIG as HUSBANDRY_CONSTANTS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { showItemGain } from '../../ui/floatingNotifications.js';
import { getZone } from '../combat/combatData.js';
import { getTalentFlatBonus, rollTalentProc, getTalentProcChance, getNodeRank, getTalentLootBonus } from '../../systems/talentTree/talentTree.js';
import { toastSuccess } from '../../ui/toast.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { architectingSkill } from '../architecting/architecting.js';
import { getItemName } from '../../data/items/index.js';
import { rollGoldenOpportunity } from '../pilfering/pilfering.js';

/**
 * Husbandry Skill - Passive pet management
 *
 * Unlike other skills, Husbandry has NO active action (no tick function).
 * Instead, it manages pets that run contracts in real-time (like farming plots).
 *
 * Core mechanics:
 * - Unlock pets at specific levels
 * - Feed pets to increase their speed bonus
 * - Evolve pets with materials to increase drop bonuses
 * - Assign pets to contracts (real-time, like farm plots)
 * - Collect rewards from loot chest when contracts complete
 */
class HusbandrySkill {
    constructor() {
        // Completion callback for UI updates
        this.onContractComplete = null;
    }

    // ==================== PET UNLOCK ====================

    /**
     * Check if a pet can be unlocked
     * @param {string} petId - Pet identifier
     * @returns {{ success: boolean, reason?: string }}
     */
    canUnlockPet(petId) {
        const petData = getPetData(petId);
        if (!petData) {
            return { success: false, reason: 'Invalid pet' };
        }

        const pet = gameState.getPet(petId);
        if (!pet) {
            return { success: false, reason: 'Pet not found' };
        }

        if (pet.unlocked) {
            return { success: false, reason: 'Already unlocked' };
        }

        const skill = gameState.getSkill(SKILLS.HUSBANDRY);
        if (skill.level < petData.unlockLevel) {
            return { success: false, reason: `Requires Husbandry level ${petData.unlockLevel}` };
        }

        // Check contract type requirements using gameState
        const result = gameState.canUnlockPet(petId, skill.level);
        if (!result.canUnlock) {
            return { success: false, reason: result.reason };
        }

        return { success: true };
    }

    /**
     * Unlock a pet
     * @param {string} petId - Pet identifier
     * @returns {{ success: boolean, reason?: string }}
     */
    unlockPet(petId) {
        const canUnlock = this.canUnlockPet(petId);
        if (!canUnlock.success) {
            return canUnlock;
        }

        gameState.unlockPet(petId);
        logger.game(`Unlocked pet: ${getPetData(petId).name}!`);
        return { success: true };
    }

    // ==================== PET EVOLUTION ====================

    /**
     * Check if a pet can evolve
     * @param {string} petId - Pet identifier
     * @returns {{ success: boolean, reason?: string, costs?: object }}
     */
    canEvolvePet(petId) {
        const pet = gameState.getPet(petId);
        if (!pet) {
            return { success: false, reason: 'Pet not found' };
        }

        if (!pet.unlocked) {
            return { success: false, reason: 'Pet not unlocked' };
        }

        if (pet.evolution >= 2) {
            return { success: false, reason: 'Already max evolution' };
        }

        const costs = getEvolutionCost(petId, pet.evolution);
        if (!costs) {
            return { success: false, reason: 'Evolution data not found' };
        }

        // Check if player has all required materials
        const missingMaterials = [];
        for (const [itemId, qty] of Object.entries(costs)) {
            const playerQty = gameState.getItemCount(itemId);
            if (playerQty < qty) {
                const itemName = itemId.replace(/_/g, ' ');
                missingMaterials.push(`${itemName}: ${playerQty}/${qty}`);
            }
        }

        if (missingMaterials.length > 0) {
            return {
                success: false,
                reason: `Missing: ${missingMaterials.join(', ')}`,
                costs
            };
        }

        return { success: true, costs };
    }

    /**
     * Evolve a pet to the next tier
     * @param {string} petId - Pet identifier
     * @returns {{ success: boolean, reason?: string }}
     */
    evolvePet(petId) {
        const canEvolve = this.canEvolvePet(petId);
        if (!canEvolve.success) {
            return canEvolve;
        }

        const pet = gameState.getPet(petId);
        const petData = getPetData(petId);

        // Consume materials
        for (const [itemId, qty] of Object.entries(canEvolve.costs)) {
            gameState.removeItem(itemId, qty);
        }

        // Evolve the pet
        gameState.evolvePet(petId);

        const newEvolution = pet.evolution;
        const evolutionName = petData.evolutions[newEvolution];

        logger.game(`${petData.name} evolved to ${evolutionName}!`);

        return { success: true };
    }

    // ==================== PET FEEDING ====================

    /**
     * Feed a pet to increase its speed bonus
     * Food type is determined by pet's evolution tier:
     * - Tier 1: Basic pet food
     * - Tier 2: Refined pet food
     * - Tier 3: Condensed pet food
     * @param {string} petId - Pet identifier
     * @param {number} amount - Amount of food items to use
     * @returns {{ success: boolean, reason?: string, foodUsed?: number, foodId?: string }}
     */
    feedPet(petId, amount = 1) {
        const pet = gameState.getPet(petId);
        if (!pet) {
            return { success: false, reason: 'Pet not found' };
        }

        if (!pet.unlocked) {
            return { success: false, reason: 'Pet not unlocked' };
        }

        // Get required food for this evolution tier and pet type
        const evolution = pet.evolution || 0;
        const requiredFood = getRequiredFood(evolution, petId);
        const foodId = requiredFood.foodId;
        const maxFood = requiredFood.maxFood;

        // Check food cap based on evolution
        const currentFood = pet.foodFed || 0;
        const remainingFood = Math.max(0, maxFood - currentFood);

        // If at cap, check for Feeding Overflow talent to allow overflow
        const feedingOverflowUnlocked = getTalentFlatBonus(SKILLS.HUSBANDRY, 'feedingOverflow') > 0;
        if (remainingFood <= 0 && !feedingOverflowUnlocked) {
            return { success: false, reason: 'Pet is fully fed for this evolution tier' };
        }

        // If at cap but overflow is unlocked, check overflow cap (1000 max)
        const currentOverflow = pet.foodOverflow || 0;
        const maxOverflow = 1000; // Max +10% bonus
        if (remainingFood <= 0 && currentOverflow >= maxOverflow) {
            return { success: false, reason: 'Pet overflow is maxed (+10% bonus)' };
        }

        // Check if player has the required food type
        const playerQty = gameState.getItemCount(foodId);
        if (playerQty <= 0) {
            return { success: false, reason: `Need ${foodId.replace(/_/g, ' ')} to feed this pet` };
        }

        // Calculate how much food to use
        let actualFood;
        let overflowFood = 0;

        if (remainingFood > 0) {
            // Still filling up the normal food cap
            actualFood = Math.min(amount, playerQty, remainingFood);
        } else if (feedingOverflowUnlocked) {
            // At cap but overflow is available
            const remainingOverflow = maxOverflow - currentOverflow;
            actualFood = Math.min(amount, playerQty, remainingOverflow);
            overflowFood = actualFood;
            actualFood = 0; // Don't add to regular food
        } else {
            actualFood = 0;
        }

        if (actualFood <= 0 && overflowFood <= 0) {
            return { success: false, reason: `Need ${foodId.replace(/_/g, ' ')} to feed this pet` };
        }

        // Consume food
        const totalFoodUsed = actualFood + overflowFood;
        gameState.removeItem(foodId, totalFoodUsed);

        // Update pet's food progress
        if (actualFood > 0) {
            gameState.feedPet(petId, actualFood);
        }

        // Update overflow if applicable
        if (overflowFood > 0) {
            gameState.addPetFoodOverflow(petId, overflowFood);
        }

        const petData = getPetData(petId);
        if (overflowFood > 0) {
            logger.game(`Fed ${petData.name} ${overflowFood} overflow ${foodId.replace(/_/g, ' ')}`);
        } else {
            logger.game(`Fed ${petData.name} ${actualFood} ${foodId.replace(/_/g, ' ')}`);
        }

        return { success: true, foodUsed: totalFoodUsed, foodId, overflowAdded: overflowFood };
    }

    /**
     * Get the total speed bonus for a pet (from feeding + evolution + talents)
     * @param {string} petId - Pet identifier
     * @returns {number} Speed bonus multiplier (0 to 0.90, capped)
     */
    getPetSpeedBonus(petId) {
        const pet = gameState.getPet(petId);
        if (!pet || !pet.unlocked) return 0;

        const evolution = pet.evolution || 0;
        const foodFed = pet.foodFed || 0;
        let feedBonus = getFeedSpeedBonus(foodFed, evolution, petId);

        // Apply Efficient Feeding talent (increases food effectiveness)
        const feedingEfficiency = getTalentFlatBonus(SKILLS.HUSBANDRY, 'feedingEfficiency');
        if (feedingEfficiency > 0) {
            feedBonus *= (1 + feedingEfficiency);
        }

        const evolutionBonus = EVOLUTION_BONUSES[evolution]?.speedBonus || 0;

        // Apply Swift Contracts talent (reduces contract duration = effective speed bonus)
        const contractSpeed = getTalentFlatBonus(SKILLS.HUSBANDRY, 'contractSpeed');

        // Apply Pack Bond talent (+5% speed per additional active pet)
        let packBondBonus = 0;
        const packBondSpeed = getTalentFlatBonus(SKILLS.HUSBANDRY, 'packBondSpeed');
        if (packBondSpeed > 0) {
            const activePetCount = this.getActivePetCount();
            // Bonus applies when there are other pets on contracts (not counting self)
            const otherActivePets = Math.max(0, activePetCount - 1);
            packBondBonus = otherActivePets * packBondSpeed;
            // Cap at 15% (3 other pets at 5% each)
            packBondBonus = Math.min(packBondBonus, 0.15);
        }

        // Apply Feeding Overflow talent (+1% speed per 100 overflow, max +10%)
        let overflowBonus = 0;
        const feedingOverflow = getTalentFlatBonus(SKILLS.HUSBANDRY, 'feedingOverflow');
        if (feedingOverflow > 0) {
            const overflow = pet.foodOverflow || 0;
            overflowBonus = Math.min(Math.floor(overflow / 100) * 0.01, 0.10);
        }

        // Apply Pet Pantry milestone speed bonus (+2% per tier, up to 6%)
        // Convert multiplier to additive percentage for existing system
        const milestoneMultiplier = gameState.getPetContractSpeedMultiplier();
        const milestoneBonus = 1 - (1 / milestoneMultiplier);

        const totalBonus = feedBonus + evolutionBonus + contractSpeed + packBondBonus + overflowBonus + milestoneBonus;
        // Cap at 90% to prevent zero/negative effective duration
        return Math.min(totalBonus, 0.90);
    }

    /**
     * Get current husbandry tier level based on Beastmaster's Lodge milestones
     * @returns {number} Tier level (0-3)
     */
    getHusbandryTierLevel() {
        let tier = 0;
        if (gameState.hasMilestone('beastmasters_lodge_i')) tier = 1;
        if (gameState.hasMilestone('beastmasters_lodge_ii')) tier = 2;
        if (gameState.hasMilestone('beastmasters_lodge_iii')) tier = 3;
        return tier;
    }

    /**
     * Add contract reward to loot chest or auto-loot to bank
     * @param {string} itemId - Item to add
     * @param {number} quantity - Amount to add
     * @returns {object} { success, added, destination }
     */
    addContractReward(itemId, quantity) {
        if (gameState.hasAutoLootChests()) {
            // Try auto-loot directly to bank (seeds to BloomHold first)
            const result = gameState.autoLootToBank(itemId, quantity, 'husbandry');
            if (result.success && result.added > 0) {
                // Show floating notification for auto-looted items
                showItemGain(itemId, result.added);
                return result;
            }
            // Fall back to loot chest if bank/bloomhold is full
            const chestResult = gameState.addLootChestItem(itemId, quantity);
            if (chestResult.success && chestResult.added > 0) {
                showItemGain(itemId, chestResult.added);
            }
            return { ...chestResult, destination: 'chest' };
        } else {
            // Add to loot chest as usual
            const result = gameState.addLootChestItem(itemId, quantity);
            return { ...result, destination: 'chest' };
        }
    }

    /**
     * Get count of pets currently on contracts
     * @returns {number} Number of active pets
     */
    getActivePetCount() {
        const pets = gameState.getPets();
        return pets.filter(pet => pet.unlocked && (pet.contractId || pet.secondaryContractId)).length;
    }

    /**
     * Check if a pet is eligible for dual contracts (Eternal Bond talent)
     * @param {string} petId - Pet identifier
     * @returns {boolean} Whether pet can have a secondary contract
     */
    canPetDualContract(petId) {
        const pet = gameState.getPet(petId);
        if (!pet) return false;
        const hasDualContracts = getTalentFlatBonus(SKILLS.HUSBANDRY, 'dualContracts') > 0;
        return hasDualContracts && pet.evolution >= 2;
    }

    /**
     * Get the drop multiplier for a pet (from evolution)
     * @param {string} petId - Pet identifier
     * @returns {number} Drop multiplier (1.0 to 1.5)
     */
    getPetDropMultiplier(petId) {
        const pet = gameState.getPet(petId);
        if (!pet || !pet.unlocked) return 1.0;

        return EVOLUTION_BONUSES[pet.evolution]?.dropMultiplier || 1.0;
    }

    // ==================== PET CONTRACTS ====================

    /**
     * Get contracts available for a pet
     * @param {string} petId - Pet identifier
     * @returns {object[]} Available contracts
     */
    getAvailableContracts(petId) {
        return getContractsForPet(petId);
    }

    /**
     * Check if a pet can start a contract
     * @param {string} petId - Pet identifier
     * @param {string} contractId - Contract identifier
     * @returns {{ success: boolean, reason?: string }}
     */
    canAssignContract(petId, contractId) {
        const pet = gameState.getPet(petId);
        if (!pet) {
            return { success: false, reason: 'Pet not found' };
        }

        if (!pet.unlocked) {
            return { success: false, reason: 'Pet not unlocked' };
        }

        const contract = PET_CONTRACTS[contractId.toUpperCase()];
        if (!contract) {
            return { success: false, reason: 'Invalid contract' };
        }

        // Check if pet can do this contract type
        const petData = getPetData(petId);

        // Check for Eidolon-only contracts (Veil Expeditions)
        if (contract.eidolonOnly && petData.role !== 'elite') {
            return { success: false, reason: `Only Eidolon can undertake ${contract.name}` };
        }

        // Check contract type compatibility
        if (petData.contractType !== 'all' && petData.contractType !== contract.type && !contract.eidolonOnly) {
            return { success: false, reason: `${petData.name} cannot do ${contract.type} contracts` };
        }

        // Check slot availability
        if (pet.contractId) {
            // Primary slot is full - check if secondary slot is available
            const hasDualContracts = getTalentFlatBonus(SKILLS.HUSBANDRY, 'dualContracts') > 0;
            if (!hasDualContracts) {
                return { success: false, reason: 'Pet already on a contract' };
            }
            if (pet.evolution < 2) {
                return { success: false, reason: 'Pet must be fully awakened for dual contracts' };
            }
            if (pet.secondaryContractId) {
                return { success: false, reason: 'Both contract slots are full' };
            }
            // Cannot assign same contract to both slots
            if (pet.contractId === contractId.toUpperCase()) {
                return { success: false, reason: 'Must be a different contract' };
            }
            return { success: true, slot: 'secondary' };
        }

        return { success: true, slot: 'primary' };
    }

    /**
     * Assign a contract to a pet
     * @param {string} petId - Pet identifier
     * @param {string} contractId - Contract identifier
     * @returns {{ success: boolean, reason?: string, rapidDeployment?: boolean }}
     */
    assignContract(petId, contractId, forceSlot = null) {
        const canAssign = this.canAssignContract(petId, contractId);
        if (!canAssign.success) {
            return canAssign;
        }

        const upperContractId = contractId.toUpperCase();
        const slot = forceSlot || canAssign.slot;

        // Check for Rapid Deployment proc (15% chance to start 25% complete)
        let rapidDeployment = false;
        const contract = PET_CONTRACTS[upperContractId];
        const speedBonus = this.getPetSpeedBonus(petId);
        const effectiveDuration = contract.duration * (1 - speedBonus);

        if (rollTalentProc(SKILLS.HUSBANDRY, 'rapidDeployment')) {
            rapidDeployment = true;
            const headStartMs = effectiveDuration * 0.25;
            if (slot === 'secondary') {
                gameState.startSecondaryPetContract(petId, upperContractId, headStartMs);
            } else {
                gameState.startPetContract(petId, upperContractId, headStartMs);
            }
            recordTalentProc(SKILLS.HUSBANDRY, 'Rapid Deployment', '25% head start!', null);
        } else {
            if (slot === 'secondary') {
                gameState.startSecondaryPetContract(petId, upperContractId);
            } else {
                gameState.startPetContract(petId, upperContractId);
            }
        }

        // Clear any pending last contract for this slot
        if (slot === 'secondary') {
            gameState.clearPetLastSecondaryContract(petId);
        } else {
            gameState.clearPetLastContract(petId);
        }

        const petData = getPetData(petId);
        logger.game(`${petData.name} started ${contract.name}${slot === 'secondary' ? ' (secondary)' : ''}${rapidDeployment ? ' (Rapid Deployment!)' : ''}`);

        return { success: true, rapidDeployment, slot };
    }

    /**
     * Stop a pet's current contract
     * @param {string} petId - Pet identifier
     * @param {string} slot - 'primary' or 'secondary'
     * @returns {{ success: boolean, reason?: string }}
     */
    stopContract(petId, slot = 'primary') {
        const pet = gameState.getPet(petId);
        if (!pet) {
            return { success: false, reason: 'Pet not found' };
        }

        if (slot === 'secondary') {
            if (!pet.secondaryContractId) {
                return { success: false, reason: 'No secondary contract active' };
            }
            gameState.stopSecondaryPetContract(petId);
            logger.game(`Stopped ${getPetData(petId).name}'s secondary contract`);
        } else {
            if (!pet.contractId) {
                return { success: false, reason: 'Pet not on a contract' };
            }
            gameState.stopPetContract(petId);
            logger.game(`Stopped ${getPetData(petId).name}'s contract`);
        }

        return { success: true };
    }

    /**
     * Get the progress of a pet's contract (0-1)
     * @param {string} petId - Pet identifier
     * @param {string} slot - 'primary' or 'secondary'
     * @returns {number} Progress from 0 to 1
     */
    getPetContractProgress(petId, slot = 'primary') {
        const pet = gameState.getPet(petId);
        if (!pet) return 0;

        const contractId = slot === 'secondary' ? pet.secondaryContractId : pet.contractId;
        const startedAt = slot === 'secondary' ? pet.secondaryContractStartedAt : pet.contractStartedAt;
        if (!contractId || !startedAt) return 0;

        const contract = PET_CONTRACTS[contractId];
        if (!contract) return 0;

        // Apply speed bonus to reduce effective duration
        const speedBonus = this.getPetSpeedBonus(petId);
        const effectiveDuration = contract.duration * (1 - speedBonus);

        const elapsed = Date.now() - startedAt;
        return Math.min(elapsed / effectiveDuration, 1);
    }

    /**
     * Get time remaining on a pet's contract (in ms)
     * @param {string} petId - Pet identifier
     * @param {string} slot - 'primary' or 'secondary'
     * @returns {number|null} Time remaining in ms, or null if no contract
     */
    getContractTimeRemaining(petId, slot = 'primary') {
        const pet = gameState.getPet(petId);
        if (!pet) return null;

        const contractId = slot === 'secondary' ? pet.secondaryContractId : pet.contractId;
        const startedAt = slot === 'secondary' ? pet.secondaryContractStartedAt : pet.contractStartedAt;
        if (!contractId || !startedAt) return null;

        const contract = PET_CONTRACTS[contractId];
        if (!contract) return null;

        // Apply speed bonus
        const speedBonus = this.getPetSpeedBonus(petId);
        const effectiveDuration = contract.duration * (1 - speedBonus);

        const elapsed = Date.now() - startedAt;
        return Math.max(0, effectiveDuration - elapsed);
    }

    /**
     * Check if a pet's contract is complete
     * @param {string} petId - Pet identifier
     * @param {string} slot - 'primary' or 'secondary'
     * @returns {boolean}
     */
    isContractComplete(petId, slot = 'primary') {
        return this.getPetContractProgress(petId, slot) >= 1;
    }

    /**
     * Check all pets for contract completion (called from game tick)
     * Only returns pets that are ready to complete AND chest has space
     * Pets at 100% with full chest stay paused until space opens
     * @returns {Array<{petId: string, slot: string}>} Array of {petId, slot} that can complete
     */
    checkAllContractCompletions() {
        const completedPets = [];
        const pets = gameState.getPets();

        // Only complete contracts if chest has space
        if (!gameState.hasLootChestSpace()) {
            return completedPets; // Return empty - pets stay at 100% paused
        }

        for (const pet of pets) {
            if (!pet.unlocked) continue;
            if (pet.contractId && this.isContractComplete(pet.id, 'primary')) {
                completedPets.push({ petId: pet.id, slot: 'primary' });
            }
            if (pet.secondaryContractId && this.isContractComplete(pet.id, 'secondary')) {
                completedPets.push({ petId: pet.id, slot: 'secondary' });
            }
        }

        return completedPets;
    }

    /**
     * Check if a pet is waiting for chest space (at 100% but can't complete)
     * @param {string} petId - Pet identifier
     * @param {string} slot - 'primary' or 'secondary'
     * @returns {boolean}
     */
    isPetWaitingForChest(petId, slot = 'primary') {
        const pet = gameState.getPet(petId);
        if (!pet) return false;
        const contractId = slot === 'secondary' ? pet.secondaryContractId : pet.contractId;
        if (!contractId) return false;
        return this.isContractComplete(petId, slot) && !gameState.hasLootChestSpace();
    }

    /**
     * Complete a pet's contract - award XP and loot
     * @param {string} petId - Pet identifier
     * @param {string} slot - 'primary' or 'secondary'
     * @returns {{ success: boolean, xpResult?: object, rewards?: object[], restartedContract?: boolean }}
     */
    completeContract(petId, slot = 'primary') {
        const pet = gameState.getPet(petId);
        const contractId = slot === 'secondary' ? pet?.secondaryContractId : pet?.contractId;
        if (!pet || !contractId) {
            return { success: false, reason: 'No contract to complete' };
        }

        if (!this.isContractComplete(petId, slot)) {
            return { success: false, reason: 'Contract not complete' };
        }

        const contract = PET_CONTRACTS[contractId];
        const petData = getPetData(petId);
        const savedContractId = contractId;

        // Clear contract before awarding (so restart can work)
        if (slot === 'secondary') {
            gameState.stopSecondaryPetContract(petId);
        } else {
            gameState.stopPetContract(petId);
        }

        // Award XP based on contract type
        // Resource contracts give Gatherer + Husbandry XP
        // Combat contracts give Bounty + Husbandry XP
        // Purification contracts give Husbandry XP only
        // Skip corruption roll - pet contracts are passive, not active player actions

        // Check for Eager Learner proc (double contract XP)
        let xpMultiplier = 1;
        if (rollTalentProc(SKILLS.HUSBANDRY, 'doubleContractXP')) {
            xpMultiplier = 2;
            recordTalentProc(SKILLS.HUSBANDRY, 'Eager Learner', '2x contract XP!', null);
        }

        const xpResults = {};
        xpResults.husbandry = addXP(SKILLS.HUSBANDRY, Math.floor(contract.xp * xpMultiplier), contract.id, { skipCorruption: true });

        if (contract.type === 'resource') {
            xpResults.gatherer = addXP(SKILLS.GATHERER, Math.floor(contract.xp * xpMultiplier), contract.id, { skipCorruption: true });
        } else if (contract.type === 'combat') {
            xpResults.bounty = addXP(SKILLS.BOUNTY, Math.floor(contract.xp * xpMultiplier), contract.id, { skipCorruption: true });
        } else if (contract.type === 'veil') {
            // Veil Expeditions give bonus XP to both Gatherer and Bounty (Eidolon specialty)
            xpResults.gatherer = addXP(SKILLS.GATHERER, Math.floor(contract.xp * 0.5 * xpMultiplier), contract.id, { skipCorruption: true });
            xpResults.bounty = addXP(SKILLS.BOUNTY, Math.floor(contract.xp * 0.5 * xpMultiplier), contract.id, { skipCorruption: true });
        }

        // For backward compatibility, keep xpResult as the husbandry result
        const xpResult = xpResults.husbandry;

        // Mark contract type as completed (for pet unlocks)
        gameState.markContractTypeCompleted(contract.type);

        // Calculate rewards with drop multiplier and Bountiful Haul talent
        const dropMultiplier = this.getPetDropMultiplier(petId);
        const chestLootBonus = getTalentFlatBonus(SKILLS.HUSBANDRY, 'chestLootBonus');
        const totalLootMultiplier = dropMultiplier * (1 + chestLootBonus);
        const rewards = [];

        for (const reward of contract.rewards) {
            if (Math.random() < reward.chance) {
                // Random quantity within range, multiplied by drop bonus and chest loot bonus
                const baseQty = Math.floor(Math.random() * (reward.maxQty - reward.minQty + 1)) + reward.minQty;
                const finalQty = Math.floor(baseQty * totalLootMultiplier);

                if (finalQty > 0) {
                    // Add to loot chest (or auto-loot to bank)
                    const result = this.addContractReward(reward.itemId, finalQty);
                    if (result.success) {
                        rewards.push({ itemId: reward.itemId, quantity: result.added, destination: result.destination });
                    }
                }
            }
        }

        // Guarantee at least one reward - pick a random one if all rolls failed
        if (rewards.length === 0 && contract.rewards.length > 0) {
            const guaranteedReward = contract.rewards[Math.floor(Math.random() * contract.rewards.length)];
            const baseQty = Math.floor(Math.random() * (guaranteedReward.maxQty - guaranteedReward.minQty + 1)) + guaranteedReward.minQty;
            const finalQty = Math.max(1, Math.floor(baseQty * totalLootMultiplier));

            const result = this.addContractReward(guaranteedReward.itemId, finalQty);
            if (result.success) {
                rewards.push({ itemId: guaranteedReward.itemId, quantity: result.added, destination: result.destination });
            }
        }

        // Roll for tier bonus rewards (Beastmaster's Lodge milestones)
        const tierLevel = this.getHusbandryTierLevel();
        if (tierLevel > 0) {
            const tierBonusRewards = getContractTierRewards(contract.type, tierLevel);
            for (const tierReward of tierBonusRewards) {
                if (Math.random() < tierReward.chance) {
                    const baseQty = Math.floor(Math.random() * (tierReward.maxQty - tierReward.minQty + 1)) + tierReward.minQty;
                    const finalQty = Math.floor(baseQty * totalLootMultiplier);

                    if (finalQty > 0) {
                        const result = this.addContractReward(tierReward.itemId, finalQty);
                        if (result.success) {
                            rewards.push({ itemId: tierReward.itemId, quantity: result.added, isTierBonus: true, destination: result.destination });
                        }
                    }
                }
            }
        }

        // Roll for insight drop based on pet type and contract
        const insightReward = this.rollInsightDrop(petId, contract);
        if (insightReward) {
            const result = this.addContractReward(insightReward.itemId, 1);
            if (result.success) {
                rewards.push({ itemId: insightReward.itemId, quantity: 1, isInsight: true, destination: result.destination });
                const insightName = getItemName(insightReward.itemId);
                logger.game(`${petData.name} found a ${insightName}!`);
            }
        }

        // Grant mob research if combat contract and pet has patrol zone
        // Research progress scales with contract duration (longer = more progress)
        let researchedMobs = [];
        if (contract.type === 'combat' && pet.patrolZone) {
            const zone = getZone(pet.patrolZone);
            if (zone && zone.mobs) {
                // Determine contract duration type for research scaling
                const durations = PET_CONFIG.CONTRACT_DURATIONS;
                let durationKey = 'short';
                if (contract.duration >= durations.long) {
                    durationKey = 'long';
                } else if (contract.duration >= durations.medium) {
                    durationKey = 'medium';
                }
                const researchProgress = PET_CONFIG.RESEARCH_PROGRESS[durationKey] || 1;

                for (const mobId of zone.mobs) {
                    gameState.addMobResearch(mobId, researchProgress);
                    researchedMobs.push(mobId);
                }
                logger.debug(`[HUSBANDRY] ${petData.name} researched mobs in ${zone.name} (+${researchProgress}): ${researchedMobs.join(', ')}`);

                // Check if zone is now fully researched (all mobs at level 2 = 6 points)
                const allMobsResearched = zone.mobs.every(mobId => gameState.getMobResearchLevel(mobId) >= 2);
                if (allMobsResearched) {
                    // Auto-stop patrol - zone research is complete
                    gameState.setPetPatrolZone(petId, null);
                    logger.game(`${petData.name} completed research in ${zone.name}! Patrol ended.`);
                }
            }
        }

        // Gift Giver talent - 10% chance for bonus item based on pet role
        let giftItem = null;
        if (rollTalentProc(SKILLS.HUSBANDRY, 'giftGiver')) {
            giftItem = this.rollGiftItem(petData.role);
            if (giftItem) {
                const result = this.addContractReward(giftItem.itemId, giftItem.quantity);
                if (result.success) {
                    rewards.push({ itemId: giftItem.itemId, quantity: result.added, isGift: true, destination: result.destination });
                    recordTalentProc(SKILLS.HUSBANDRY, 'Gift Giver', null, null, { itemId: giftItem.itemId, quantity: result.added });
                }
            }
        }

        // Talent Synergy System - pets "learn" from your talents and can find those items
        const talentBonuses = this.rollTalentSynergyBonuses(petId, contract);
        for (const bonus of talentBonuses) {
            const result = this.addContractReward(bonus.itemId, bonus.quantity);
            if (result.success) {
                rewards.push({
                    itemId: bonus.itemId,
                    quantity: result.added,
                    isTalentSynergy: true,
                    talentSource: bonus.talentSource,
                    destination: result.destination
                });
                // Record the talent proc for activity tracking
                recordTalentProc(SKILLS.HUSBANDRY, `Pet Synergy: ${bonus.talentSource}`, null, null, {
                    itemId: bonus.itemId,
                    quantity: result.added
                });
            }
        }

        // Golden Opportunity talent - bonus gold for auto-looted items
        if (gameState.hasAutoLootChests() && rewards.length > 0) {
            // Filter to only items that were auto-looted to bank (not chest)
            const autoLootedItems = rewards.filter(r => r.destination === 'bank');
            if (autoLootedItems.length > 0) {
                rollGoldenOpportunity(autoLootedItems);
            }
        }

        // Pack Instinct talent - other active pets gain 15% progress
        const packInstinctChance = getTalentProcChance(SKILLS.HUSBANDRY, 'packInstinct');
        if (packInstinctChance > 0) {
            this.applyPackInstinct(petId, packInstinctChance);
        }

        // Build XP log message based on contract type
        let xpLogParts = [`+${contract.xp} Husbandry XP`];
        if (contract.type === 'resource') {
            xpLogParts.push(`+${contract.xp} Gatherer XP`);
        } else if (contract.type === 'combat') {
            xpLogParts.push(`+${contract.xp} Bounty XP`);
        } else if (contract.type === 'veil') {
            xpLogParts.push(`+${Math.floor(contract.xp * 0.5)} Gatherer XP`);
            xpLogParts.push(`+${Math.floor(contract.xp * 0.5)} Bounty XP`);
        }
        logger.game(`${petData.name} completed ${contract.name}! ${xpLogParts.join(', ')}`);

        // Try to restart the same contract in the same slot if loot chest has space
        let restartedContract = false;
        if (gameState.hasLootChestSpace()) {
            const restartResult = this.assignContract(petId, savedContractId, slot);
            restartedContract = restartResult.success;
        }

        // If restart failed (likely due to full chest), remember the contract for later
        if (!restartedContract) {
            if (slot === 'secondary') {
                gameState.setPetLastSecondaryContract(petId, savedContractId);
            } else {
                gameState.setPetLastContract(petId, savedContractId);
            }
        }

        // Call completion callback
        if (this.onContractComplete) {
            this.onContractComplete(petId, contract, xpResult, rewards, restartedContract, slot);
        }

        return { success: true, xpResult, rewards, restartedContract, slot };
    }

    // ==================== LOOT CHEST ====================

    /**
     * Get loot chest contents
     * @returns {object[]} Array of { itemId, quantity }
     */
    getLootChest() {
        return gameState.player.lootChest;
    }

    /**
     * Get loot chest stats
     * @returns {{ usedSlots: number, maxSlots: number, items: object[], bonusSlots: number }}
     */
    getLootChestStats() {
        const maxSlots = gameState.getLootChestMaxSlots();
        const bonusSlots = gameState.getPetChestStorageBonus();
        return {
            usedSlots: gameState.getLootChestUniqueCount(),
            maxSlots,
            items: this.getLootChest(),
            bonusSlots
        };
    }

    /**
     * Claim an item from loot chest to inventory
     * @param {string} itemId - Item identifier
     * @param {number} amount - Amount to claim
     * @returns {{ success: boolean, claimed: number, reason?: string }}
     */
    claimFromChest(itemId, amount = 1) {
        const chestQty = gameState.getLootChestItemCount(itemId);
        if (chestQty < amount) {
            return { success: false, claimed: 0, reason: 'Not enough in chest' };
        }

        // Try to add to inventory
        const addResult = gameState.addItem(itemId, amount);
        if (!addResult.success) {
            return { success: false, claimed: 0, reason: addResult.reason };
        }

        // Remove from chest
        gameState.removeLootChestItem(itemId, addResult.added);

        // Show floating notification
        showItemGain(itemId, addResult.added);

        // Award architecting XP from Beast Keeper talent (if unlocked)
        architectingSkill.awardPetChestXP();

        // Try to resume idle pets now that chest has space
        this.resumeIdlePets();

        return { success: true, claimed: addResult.added };
    }

    /**
     * Claim all items from loot chest to inventory
     * @returns {{ success: boolean, claimedItems: object[] }}
     */
    claimAllFromChest() {
        const chest = this.getLootChest();
        const claimedItems = [];

        for (const slot of [...chest]) { // Clone array since we're modifying it
            // Use direct claim logic to avoid multiple resumeIdlePets calls
            const chestQty = gameState.getLootChestItemCount(slot.itemId);
            if (chestQty <= 0) continue;

            const addResult = gameState.addItem(slot.itemId, slot.quantity);
            if (addResult.success && addResult.added > 0) {
                gameState.removeLootChestItem(slot.itemId, addResult.added);
                showItemGain(slot.itemId, addResult.added);
                claimedItems.push({ itemId: slot.itemId, quantity: addResult.added });
                // Award architecting XP from Beast Keeper talent (if unlocked)
                architectingSkill.awardPetChestXP();
            }
        }

        // Resume idle pets after all claims are done
        this.resumeIdlePets();

        return { success: true, claimedItems };
    }

    /**
     * Resume idle pets that were waiting for chest space
     * Called after claiming items from the loot chest
     */
    resumeIdlePets() {
        if (!gameState.hasLootChestSpace()) return;

        const idlePets = gameState.getIdlePetsWithLastContract();
        for (const { petId, lastContractId, slot } of idlePets) {
            if (!gameState.hasLootChestSpace()) break; // Stop if chest fills up again

            const result = this.assignContract(petId, lastContractId, slot);
            if (result.success) {
                if (slot === 'secondary') {
                    gameState.clearPetLastSecondaryContract(petId);
                } else {
                    gameState.clearPetLastContract(petId);
                }
                const petData = getPetData(petId);
                logger.game(`${petData.name} resumed their ${slot === 'secondary' ? 'secondary ' : ''}contract`);
            }
        }
    }

    // ==================== UTILITY ====================

    /**
     * Get all unlocked pets
     * @returns {object[]} Array of pet objects
     */
    getUnlockedPets() {
        return gameState.getPets().filter(pet => pet.unlocked);
    }

    /**
     * Get all pets with their data
     * @returns {object[]} Array of { pet, data }
     */
    getAllPetsWithData() {
        return gameState.getPets().map(pet => ({
            pet,
            data: getPetData(pet.id)
        }));
    }

    // ==================== TALENT HELPERS ====================

    /**
     * Roll for an insight drop based on pet type and contract
     * @param {string} petId - Pet identifier
     * @param {object} contract - Contract data
     * @returns {{ itemId: string } | null}
     */
    rollInsightDrop(petId, contract) {
        // Check if contract has insight drop chances
        if (!contract.insightChance || !contract.insightTiers) {
            return null;
        }

        const pet = gameState.getPet(petId);
        if (!pet) return null;

        // Get the insight drop chance for this pet type
        const dropChance = contract.insightChance[pet.id] || 0;
        if (dropChance <= 0) return null;

        // Apply Treasure Nose talent bonus (+3% per rank to rare loot chance)
        const treasureNoseBonus = getTalentFlatBonus(SKILLS.HUSBANDRY, 'globalRareLoot');
        const adjustedChance = dropChance + treasureNoseBonus;

        // Roll for insight drop
        if (Math.random() >= adjustedChance) {
            return null;
        }

        // Get available insight tiers for this pet type
        const insightTiers = contract.insightTiers[pet.id];
        if (!insightTiers || insightTiers.length === 0) {
            return null;
        }

        // Pick a random insight from available tiers
        // Higher tier insights are weighted lower
        let selectedInsight;
        if (insightTiers.length === 1) {
            selectedInsight = insightTiers[0];
        } else {
            // Weight towards lower tier insights (first items in array are more common)
            // 70% chance for first tier, 30% for second (or split evenly if more)
            if (Math.random() < 0.7) {
                selectedInsight = insightTiers[0];
            } else {
                selectedInsight = insightTiers[insightTiers.length - 1];
            }
        }

        return { itemId: selectedInsight };
    }

    /**
     * Roll a gift item based on pet role (for Gift Giver talent)
     * @param {string} role - Pet role (gatherer, combat, purifier, hybrid, elite)
     * @returns {{ itemId: string, quantity: number } | null}
     */
    rollGiftItem(role) {
        // 70% common, 30% rare
        const isRare = Math.random() < 0.30;

        // Define drop pools by pet role
        const giftPools = {
            gatherer: {
                common: [
                    { itemId: 'ironwood_seed', qty: [1, 2] },
                    { itemId: 'softbark_seed', qty: [1, 2] },
                    { itemId: 'goldleaf_seed', qty: [1, 2] },
                    { itemId: 'grimy_sanctum_leaf', qty: [2, 5] },
                    { itemId: 'grimy_sunpetal', qty: [2, 5] },
                    { itemId: 'ironwood_logs', qty: [5, 10] },
                    { itemId: 'softbark_logs', qty: [5, 10] }
                ],
                rare: [
                    { itemId: 'arcanewood_logs', qty: [3, 8] },
                    { itemId: 'ancient_seed', qty: [1, 1] },
                    { itemId: 'grimy_crownwort', qty: [2, 4] },
                    { itemId: 'grimy_brightmaw', qty: [2, 4] },
                    { itemId: 'gravewood_seed', qty: [1, 2] }
                ]
            },
            combat: {
                common: [
                    { itemId: 'small_bone', qty: [5, 10] },
                    { itemId: 'charred_bone', qty: [3, 6] },
                    { itemId: 'treated_hide', qty: [2, 5] },
                    { itemId: 'green_wyrm_hide', qty: [1, 3] },
                    { itemId: 'raw_river_char', qty: [3, 6] }
                ],
                rare: [
                    { itemId: 'dragon_bone', qty: [1, 3] },
                    { itemId: 'void_drake_hide', qty: [1, 2] },
                    { itemId: 'black_drake_hide', qty: [1, 3] },
                    { itemId: 'red_wyrm_hide', qty: [1, 3] }
                ]
            },
            purifier: {
                common: [
                    { itemId: 'ashes', qty: [10, 20] },
                    { itemId: 'arcane_residue', qty: [5, 10] },
                    { itemId: 'arcane_residue', qty: [3, 6] },
                    { itemId: 'treated_hide', qty: [2, 4] }
                ],
                rare: [
                    { itemId: 'purified_arcane_core', qty: [1, 2] },
                    { itemId: 'refined_arcane_ichor', qty: [2, 5] },
                    { itemId: 'flame_glyph', qty: [1, 2] },
                    { itemId: 'frost_glyph', qty: [1, 2] },
                    { itemId: 'stone_glyph', qty: [1, 2] },
                    { itemId: 'storm_glyph', qty: [1, 2] }
                ]
            },
            hybrid: {
                // Chimera gets items from all pools
                common: [
                    { itemId: 'ironwood_seed', qty: [1, 2] },
                    { itemId: 'small_bone', qty: [5, 10] },
                    { itemId: 'ashes', qty: [10, 20] },
                    { itemId: 'grimy_sunpetal', qty: [2, 5] },
                    { itemId: 'green_wyrm_hide', qty: [1, 3] }
                ],
                rare: [
                    { itemId: 'arcanewood_logs', qty: [3, 8] },
                    { itemId: 'dragon_bone', qty: [1, 3] },
                    { itemId: 'purified_arcane_core', qty: [1, 2] },
                    { itemId: 'black_drake_hide', qty: [1, 3] }
                ]
            },
            elite: {
                // Eidolon gets awakening materials and high-tier items
                common: [
                    { itemId: 'twisted_root_fiber', qty: [5, 10] },
                    { itemId: 'living_arcane_husk', qty: [2, 5] },
                    { itemId: 'arcane_residue', qty: [8, 15] },
                    { itemId: 'refined_arcane_ichor', qty: [2, 4] }
                ],
                rare: [
                    { itemId: 'condensed_arcane_core_fragment', qty: [1, 3] },
                    { itemId: 'entropy_glyph', qty: [1, 1] },
                    { itemId: 'void_glyph', qty: [1, 1] },
                    { itemId: 'binding_glyph', qty: [1, 1] },
                    { itemId: 'doom_glyph', qty: [1, 1] },
                    { itemId: 'void_drake_hide', qty: [1, 2] }
                ]
            }
        };

        const pool = giftPools[role] || giftPools.hybrid;
        const items = isRare ? pool.rare : pool.common;

        if (!items || items.length === 0) return null;

        // Pick random item from pool
        const chosen = items[Math.floor(Math.random() * items.length)];
        const quantity = Math.floor(Math.random() * (chosen.qty[1] - chosen.qty[0] + 1)) + chosen.qty[0];

        return { itemId: chosen.itemId, quantity };
    }

    /**
     * Apply Pack Instinct talent - give other active pets progress boost
     * Boosts both primary and secondary contract slots
     * @param {string} completingPetId - Pet that just completed contract
     * @param {number} progressBoost - Progress boost amount (0.15 = 15%)
     */
    applyPackInstinct(completingPetId, progressBoost) {
        const MAX_BOOSTED_PROGRESS = 0.95; // Cap: Pack Instinct can't push past 95%
        const pets = gameState.getPets();

        for (const pet of pets) {
            // Skip the pet that completed the contract and inactive pets
            if (pet.id === completingPetId || !pet.unlocked) continue;

            const speedBonus = this.getPetSpeedBonus(pet.id);

            // Boost primary contract (capped at 95% to prevent chain completion cascades)
            if (pet.contractId && pet.contractStartedAt) {
                const contract = PET_CONTRACTS[pet.contractId];
                if (contract) {
                    const effectiveDuration = contract.duration * (1 - speedBonus);
                    const currentProgress = Math.min((Date.now() - pet.contractStartedAt) / effectiveDuration, 1);
                    if (currentProgress < MAX_BOOSTED_PROGRESS) {
                        const targetProgress = Math.min(currentProgress + progressBoost, MAX_BOOSTED_PROGRESS);
                        const actualBoost = targetProgress - currentProgress;
                        const progressMs = effectiveDuration * actualBoost;
                        gameState.boostPetContractProgress(pet.id, progressMs, 'primary');
                    }
                }
            }

            // Boost secondary contract (same 95% cap)
            if (pet.secondaryContractId && pet.secondaryContractStartedAt) {
                const secondaryContract = PET_CONTRACTS[pet.secondaryContractId];
                if (secondaryContract) {
                    const effectiveDuration = secondaryContract.duration * (1 - speedBonus);
                    const currentProgress = Math.min((Date.now() - pet.secondaryContractStartedAt) / effectiveDuration, 1);
                    if (currentProgress < MAX_BOOSTED_PROGRESS) {
                        const targetProgress = Math.min(currentProgress + progressBoost, MAX_BOOSTED_PROGRESS);
                        const actualBoost = targetProgress - currentProgress;
                        const progressMs = effectiveDuration * actualBoost;
                        gameState.boostPetContractProgress(pet.id, progressMs, 'secondary');
                    }
                }
            }
        }
    }

    /**
     * Get the Gift Giver drop pools for UI display
     * @returns {object} Drop pools by pet role
     */
    getGiftGiverPools() {
        return {
            gatherer: {
                common: ['Seeds', 'Grimy herbs', 'Logs (ironwood-softbark)'],
                rare: ['Arcanewood logs', 'Ancient seed', 'Voidbloom', 'Elderbark']
            },
            combat: {
                common: ['Bones', 'Hides (treated-green wyrm)', 'Raw fish'],
                rare: ['Dragon bone', 'Void drake hide', 'Black drake hide']
            },
            purifier: {
                common: ['Ashes', 'Arcane residue', 'Basic ichor'],
                rare: ['Purified core', 'Refined ichor', 'Minor glyphs']
            },
            hybrid: {
                common: ['Any common from gatherer/combat/purifier'],
                rare: ['Any rare from gatherer/combat/purifier']
            },
            elite: {
                common: ['Awakening materials (T1-T2)', 'Arcane residue', 'Refined ichor'],
                rare: ['Condensed core fragments', 'Major glyphs', 'Void drake hide']
            }
        };
    }

    // ==================== TALENT SYNERGY SYSTEM ====================

    /**
     * Pet-to-talent synergy mapping
     * Defines which talent trees each pet type can benefit from
     * Chimera and Eidolon benefit from ALL talents
     */
    getTalentSynergyMapping() {
        return {
            rootling: [SKILLS.WOODCUTTING, SKILLS.GATHERER, SKILLS.FARMING, SKILLS.PYRE],
            felltracker: [SKILLS.MINING, SKILLS.FISHING, SKILLS.PYRE],
            ichorebound: [SKILLS.ALCHEMY, SKILLS.GLYPHBINDING, SKILLS.PYRE],
            chimera: 'all', // Benefits from ALL talents
            eidolon: 'all'  // Benefits from ALL talents
        };
    }

    /**
     * Roll for talent synergy bonus items based on player talents and pet type
     * This is the main talent synergy function - pets "learn" from your talents
     * @param {string} petId - Pet identifier
     * @param {object} contract - Contract data
     * @returns {object[]} Array of bonus items { itemId, quantity, talentSource }
     */
    rollTalentSynergyBonuses(petId, contract) {
        const pet = gameState.getPet(petId);
        if (!pet) return [];

        const petData = getPetData(petId);
        const synergies = this.getTalentSynergyMapping();
        const petSynergies = synergies[pet.id];
        const bonusItems = [];

        // Determine which skills to check for talents
        const skillsToCheck = petSynergies === 'all'
            ? [SKILLS.WOODCUTTING, SKILLS.MINING, SKILLS.FISHING, SKILLS.GATHERER, SKILLS.FARMING, SKILLS.ALCHEMY, SKILLS.PYRE, SKILLS.GLYPHBINDING]
            : petSynergies;

        // Check each relevant skill's talents
        for (const skillId of skillsToCheck) {
            const synergizedItems = this.checkSkillTalentSynergies(skillId, petData.role, contract);
            bonusItems.push(...synergizedItems);
        }

        return bonusItems;
    }

    /**
     * Check a specific skill's talents for pet synergies
     * @param {string} skillId - Skill to check talents for
     * @param {string} petRole - Pet's role (gatherer, combat, purifier, hybrid, elite)
     * @param {object} contract - Contract data
     * @returns {object[]} Bonus items from this skill's talents
     */
    checkSkillTalentSynergies(skillId, petRole, contract) {
        const bonusItems = [];

        switch (skillId) {
            case SKILLS.WOODCUTTING:
                bonusItems.push(...this.checkWoodcuttingTalents(petRole));
                break;
            case SKILLS.MINING:
                bonusItems.push(...this.checkMiningTalents(petRole));
                break;
            case SKILLS.FISHING:
                bonusItems.push(...this.checkFishingTalents(petRole));
                break;
            case SKILLS.GATHERER:
                bonusItems.push(...this.checkGathererTalents(petRole));
                break;
            case SKILLS.FARMING:
                bonusItems.push(...this.checkFarmingTalents(petRole));
                break;
            case SKILLS.ALCHEMY:
                bonusItems.push(...this.checkAlchemyTalents(petRole));
                break;
            case SKILLS.PYRE:
                bonusItems.push(...this.checkPyreTalents(petRole));
                break;
            case SKILLS.GLYPHBINDING:
                bonusItems.push(...this.checkGlyphbindingTalents(petRole));
                break;
        }

        return bonusItems;
    }

    /**
     * Check Woodcutting talents: Deep Roots (seeds), Kindling Master (ashes)
     */
    checkWoodcuttingTalents(petRole) {
        const items = [];

        // Deep Roots - tree seeds and ancient seeds
        const deepRootsRank = getNodeRank(SKILLS.WOODCUTTING, 'woodcutting_roots');
        if (deepRootsRank > 0) {
            // 4% per rank chance for tree seeds
            const seedChance = 0.04 * deepRootsRank;
            if (Math.random() < seedChance) {
                const treeSeedPool = ['softbark_seed', 'ironwood_seed', 'goldleaf_seed', 'arcanewood_seed'];
                const seed = treeSeedPool[Math.floor(Math.random() * treeSeedPool.length)];
                items.push({ itemId: seed, quantity: 1, talentSource: 'Deep Roots' });
            }

            // 0.5% per rank chance for ancient seed (at max rank = 2 ranks = 1%)
            const ancientSeedChance = 0.005 * deepRootsRank;
            if (Math.random() < ancientSeedChance) {
                items.push({ itemId: 'ancient_seed', quantity: 1, talentSource: 'Deep Roots' });
            }
        }

        // Kindling Master - bonus ashes
        const kindlingRank = getNodeRank(SKILLS.WOODCUTTING, 'woodcutting_kindling');
        if (kindlingRank > 0) {
            // 25% chance for bonus ashes
            if (Math.random() < 0.25) {
                const qty = Math.floor(Math.random() * 5) + 2; // 2-6 ashes
                items.push({ itemId: 'ashes', quantity: qty, talentSource: 'Kindling Master' });
            }
        }

        return items;
    }

    /**
     * Check Mining talents: Gem Finder, Rare Earth
     */
    checkMiningTalents(petRole) {
        const items = [];

        // Gem Finder - random gems
        const gemFinderRank = getNodeRank(SKILLS.MINING, 'mining_gems');
        if (gemFinderRank > 0) {
            // 5% per rank chance for gems
            const gemChance = 0.05 * gemFinderRank;
            if (Math.random() < gemChance) {
                const gemPool = ['ruby', 'sapphire', 'emerald', 'diamond'];
                const gem = gemPool[Math.floor(Math.random() * gemPool.length)];
                items.push({ itemId: gem, quantity: 1, talentSource: 'Gem Finder' });
            }
        }

        // Rare Earth - random ores
        const rareEarthRank = getNodeRank(SKILLS.MINING, 'mining_rare');
        if (rareEarthRank > 0) {
            // 10% flat chance for bonus ore
            if (Math.random() < 0.10) {
                const orePool = ['pyrestone_ore', 'pale_shard_ore', 'blackiron_ore', 'gold_ore', 'skyiron_ore'];
                const ore = orePool[Math.floor(Math.random() * orePool.length)];
                const qty = Math.floor(Math.random() * 3) + 1; // 1-3 ores
                items.push({ itemId: ore, quantity: qty, talentSource: 'Rare Earth' });
            }
        }

        return items;
    }

    /**
     * Check Fishing talents: Bycatch
     */
    checkFishingTalents(petRole) {
        const items = [];

        // Bycatch - bonus variety items
        const bycatchRank = getNodeRank(SKILLS.FISHING, 'fishing_bycatch');
        if (bycatchRank > 0) {
            // 4% per rank chance for bycatch items
            const bycatchChance = 0.04 * bycatchRank;
            if (Math.random() < bycatchChance) {
                // Bycatch pool: seeds, herbs, bones
                const bycatchPool = [
                    { itemId: 'potato_seed', qty: [1, 2] },
                    { itemId: 'cabbage_seed', qty: [1, 2] },
                    { itemId: 'grimy_ironbloom', qty: [1, 3] },
                    { itemId: 'grimy_emberroot', qty: [1, 3] },
                    { itemId: 'small_bone', qty: [2, 5] },
                    { itemId: 'charred_bone', qty: [1, 3] }
                ];
                const chosen = bycatchPool[Math.floor(Math.random() * bycatchPool.length)];
                const qty = Math.floor(Math.random() * (chosen.qty[1] - chosen.qty[0] + 1)) + chosen.qty[0];
                items.push({ itemId: chosen.itemId, quantity: qty, talentSource: 'Bycatch' });
            }
        }

        return items;
    }

    /**
     * Check Gatherer talents: Seed Finder, Herb Attunement, Awakening Specialist
     */
    checkGathererTalents(petRole) {
        const items = [];

        // Seed Finder - crop/herb seeds
        const seedFinderRank = getNodeRank(SKILLS.GATHERER, 'gatherer_seed_finder');
        if (seedFinderRank > 0) {
            // 3% per rank chance for seeds
            const seedChance = 0.03 * seedFinderRank;
            if (Math.random() < seedChance) {
                const seedPool = [
                    'potato_seed', 'cabbage_seed', 'onion_seed', 'tomato_seed',
                    'greenleaf_seed', 'duskleaf_seed', 'ironbloom_seed'
                ];
                const seed = seedPool[Math.floor(Math.random() * seedPool.length)];
                items.push({ itemId: seed, quantity: 1, talentSource: 'Seed Finder' });
            }
        }

        // Awakening Specialist - more awakening materials
        const awakeningRank = getNodeRank(SKILLS.GATHERER, 'gatherer_awakening');
        if (awakeningRank > 0) {
            // +35% awakening material chance per rank
            const awakeningChance = 0.15 * awakeningRank; // Base 15% scaled by rank
            if (Math.random() < awakeningChance) {
                const awakeningPool = [
                    { itemId: 'twisted_root_fiber', qty: [2, 5] },
                    { itemId: 'living_arcane_husk', qty: [1, 3] }
                ];
                const chosen = awakeningPool[Math.floor(Math.random() * awakeningPool.length)];
                const qty = Math.floor(Math.random() * (chosen.qty[1] - chosen.qty[0] + 1)) + chosen.qty[0];
                items.push({ itemId: chosen.itemId, quantity: qty, talentSource: 'Awakening Specialist' });
            }
        }

        return items;
    }

    /**
     * Check Farming talents: Seed Reclamation, Abundant Fields
     */
    checkFarmingTalents(petRole) {
        const items = [];

        // Seed Reclamation - extra seeds with drops
        const reclaimRank = getNodeRank(SKILLS.FARMING, 'farming_reclaim');
        if (reclaimRank > 0) {
            // 15% per rank chance for bonus seed
            const reclaimChance = 0.15 * reclaimRank;
            if (Math.random() < reclaimChance) {
                const seedPool = ['potato_seed', 'cabbage_seed', 'onion_seed', 'tomato_seed'];
                const seed = seedPool[Math.floor(Math.random() * seedPool.length)];
                items.push({ itemId: seed, quantity: 1, talentSource: 'Seed Reclamation' });
            }
        }

        // Abundant Fields - increased seed drop chance
        const abundantRank = getNodeRank(SKILLS.FARMING, 'farming_abundant');
        if (abundantRank > 0) {
            // 15% chance for bonus seed per rank
            if (Math.random() < 0.15 * abundantRank) {
                const seedPool = ['potato_seed', 'cabbage_seed', 'onion_seed', 'greenleaf_seed'];
                const seed = seedPool[Math.floor(Math.random() * seedPool.length)];
                items.push({ itemId: seed, quantity: 1, talentSource: 'Abundant Fields' });
            }
        }

        return items;
    }

    /**
     * Check Alchemy talents: Bloom Touched (BloomHold), Transmutation (upgrade)
     * Note: Bloom Touched sends herbs to BloomHold - handled separately in addToChestOrBloomHold
     */
    checkAlchemyTalents(petRole) {
        const items = [];

        // Transmutation - upgraded herb drops
        const transmuteRank = getNodeRank(SKILLS.ALCHEMY, 'alchemy_transmute');
        if (transmuteRank > 0) {
            // 5% per rank chance for upgraded herb
            const transmuteChance = 0.05 * transmuteRank;
            if (Math.random() < transmuteChance) {
                // Give a higher-tier herb
                const herbPool = [
                    { itemId: 'clean_nightvine', qty: [1, 2] },
                    { itemId: 'clean_glowstem', qty: [1, 2] },
                    { itemId: 'clean_ashbloom', qty: [1, 2] }
                ];
                const chosen = herbPool[Math.floor(Math.random() * herbPool.length)];
                const qty = Math.floor(Math.random() * (chosen.qty[1] - chosen.qty[0] + 1)) + chosen.qty[0];
                items.push({ itemId: chosen.itemId, quantity: qty, talentSource: 'Transmutation' });
            }
        }

        return items;
    }

    /**
     * Check Pyre talents: Smoke Reading (ashes), Veil's Blessing (potions)
     * All pets can benefit from Pyre talents (thematic connection to the Veil)
     */
    checkPyreTalents(petRole) {
        const items = [];

        // Smoke Reading - bonus ashes
        const smokeRank = getNodeRank(SKILLS.PYRE, 'pyre_smoke');
        if (smokeRank > 0) {
            // 5% per rank chance for bonus ashes
            const ashChance = 0.05 * smokeRank;
            if (Math.random() < ashChance) {
                const qty = Math.floor(Math.random() * 4) + 2; // 2-5 ashes
                items.push({ itemId: 'ashes', quantity: qty, talentSource: 'Smoke Reading' });
            }
        }

        // Veil's Blessing - random potion
        const blessingRank = getNodeRank(SKILLS.PYRE, 'pyre_blessing');
        if (blessingRank > 0) {
            // 5% flat chance for random potion
            if (Math.random() < 0.05) {
                const potionPool = [
                    'focused_elixir',         // Crit potion
                    'steady_brew',            // Accuracy potion
                    'feral_draft',            // Combat potion
                    'quickening_draught'      // Speed potion
                ];
                const potion = potionPool[Math.floor(Math.random() * potionPool.length)];
                items.push({ itemId: potion, quantity: 1, talentSource: "Veil's Blessing" });
            }
        }

        return items;
    }

    /**
     * Check Glyphbinding talents: Essence Siphon (ichor), Resonant Glyphs
     */
    checkGlyphbindingTalents(petRole) {
        const items = [];

        // Essence Siphon - bonus ichor
        const siphonRank = getNodeRank(SKILLS.GLYPHBINDING, 'glyphbinding_siphon');
        if (siphonRank > 0) {
            // 10% per rank chance for bonus ichor
            const ichorChance = 0.10 * siphonRank;
            if (Math.random() < ichorChance) {
                const ichorPool = [
                    { itemId: 'arcane_residue', qty: [2, 4] },
                    { itemId: 'refined_arcane_ichor', qty: [1, 2] }
                ];
                const chosen = ichorPool[Math.floor(Math.random() * ichorPool.length)];
                const qty = Math.floor(Math.random() * (chosen.qty[1] - chosen.qty[0] + 1)) + chosen.qty[0];
                items.push({ itemId: chosen.itemId, quantity: qty, talentSource: 'Essence Siphon' });
            }
        }

        // Resonant Glyphs - chance for glyphs
        const resonantRank = getNodeRank(SKILLS.GLYPHBINDING, 'glyphbinding_resonant');
        if (resonantRank > 0) {
            // 3% per rank chance for glyph
            const glyphChance = 0.03 * resonantRank;
            if (Math.random() < glyphChance) {
                const glyphPool = [
                    'storm_glyph', 'frost_glyph', 'flame_glyph', 'stone_glyph'
                ];
                const glyph = glyphPool[Math.floor(Math.random() * glyphPool.length)];
                items.push({ itemId: glyph, quantity: 1, talentSource: 'Resonant Glyphs' });
            }
        }

        return items;
    }
}

export const husbandrySkill = new HusbandrySkill();
export default husbandrySkill;
